/**
 * AdminAuthContext — bewaart admin-sessie + user, doet session-restore op mount,
 * verzorgt auto-refresh-timer en biedt login/MFA/logout-helpers.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  adminLogin, adminMe, adminLogout, adminRefresh, mfaStatus, mfaChallenge, mfaVerifyChallenge,
  loadSession, saveSession, clearSession, isExpired, scheduleRefresh, setUnauthorizedHandler,
} from "./adminApi";

const MFA_REQUIRED = String(process.env.REACT_APP_MFA_REQUIRED || "false") === "true";

const AuthContext = createContext(null);

// Geëxporteerd zodat hooks zoals useUiPreference defensief useContext kunnen
// gebruiken (returnt null buiten provider i.p.v. te crashen — handig in unit-tests).
export const AdminAuthContext = AuthContext;

export function AdminAuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [user, setUser] = useState(null);
  const [bootstrapping, setBootstrapping] = useState(true);
  const [mfaPending, setMfaPending] = useState(null); // { factorId, challengeId? }
  const refreshedAt = useRef(null);

  // Bootstrap: load existing session + me
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const s = loadSession();
      if (!s || isExpired(s)) {
        clearSession();
        if (!cancelled) setBootstrapping(false);
        return;
      }
      try {
        const me = await adminMe();
        if (cancelled) return;
        setSession(s);
        setUser(me);
        scheduleRefresh(s, _onRefreshed, _onRefreshFailed);
      } catch {
        clearSession();
      } finally {
        if (!cancelled) setBootstrapping(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 401-global-handler
  useEffect(() => {
    setUnauthorizedHandler(() => {
      setSession(null);
      setUser(null);
      setMfaPending(null);
    });
    return () => setUnauthorizedHandler(null);
  }, []);

  const _onRefreshed = useCallback((newSession) => {
    refreshedAt.current = Date.now();
    setSession(newSession);
  }, []);

  const _onRefreshFailed = useCallback(() => {
    setSession(null);
    setUser(null);
  }, []);

  const login = useCallback(async (email, password) => {
    const tokens = await adminLogin(email, password);
    saveSession(tokens);
    setSession(tokens);
    setUser(tokens.user);
    scheduleRefresh(tokens, _onRefreshed, _onRefreshFailed);

    // Check MFA-enrollment
    if (MFA_REQUIRED) {
      const status = await mfaStatus();
      const verifiedFactor = (status.factors || []).find((f) => f.status === "verified");
      if (verifiedFactor) {
        const ch = await mfaChallenge(verifiedFactor.factor_id);
        setMfaPending({ factorId: verifiedFactor.factor_id, challengeId: ch.challenge_id });
        return { mfa_required: true };
      }
    }
    return { mfa_required: false };
  }, [_onRefreshed, _onRefreshFailed]);

  const verifyMfa = useCallback(async (code) => {
    if (!mfaPending) throw new Error("Geen MFA-challenge actief");
    await mfaVerifyChallenge(mfaPending.factorId, mfaPending.challengeId, code);
    setMfaPending(null);
    return true;
  }, [mfaPending]);

  const logout = useCallback(async () => {
    await adminLogout();
    setSession(null);
    setUser(null);
    setMfaPending(null);
  }, []);

  // Patcht alleen het lokale user-object (geen API-call). Wordt gebruikt
  // door useUiPreference voor optimistic update + revert-on-failure.
  const updateLocalUserPreferences = useCallback((nextUiPreferences) => {
    setUser((prev) => (prev ? { ...prev, ui_preferences: nextUiPreferences || {} } : prev));
  }, []);

  const value = useMemo(() => ({
    session, user, bootstrapping, mfaPending,
    mfaRequired: MFA_REQUIRED,
    login, verifyMfa, logout,
    updateLocalUserPreferences,
    isAuthenticated: Boolean(session && user && !mfaPending),
  }), [session, user, bootstrapping, mfaPending, login, verifyMfa, logout, updateLocalUserPreferences]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAdminAuth() {
  const v = useContext(AuthContext);
  if (!v) throw new Error("useAdminAuth must be used within AdminAuthProvider");
  return v;
}
